// panelFiltros.ts — el panel «Filtros» de la barra del calendario admin
// (round 2026-09-15): cuántos filtros hay activos y si el panel está abierto.
import { useState } from "react";
import type { PropFilters } from "./useCalendarioLogic";

/** Clave de localStorage con la última elección, por separado para celular y escritorio. */
export const CLAVE_PANEL_FILTROS = "cal_panel_filtros";

type Preferencia = { movil?: boolean; escritorio?: boolean };

export const filtrosVacios = (): PropFilters => ({
  region: [],
  comuna: [],
  sector: [],
  cliente: [],
});

/**
 * Cuenta las búsquedas con texto y cada dimensión (región, comuna, sector,
 * cliente) con al menos un valor. El orden y el buscador por código no dejan
 * filas afuera, así que no cuentan.
 */
export function contarFiltrosActivos(
  propSearch: string,
  guestSearch: string,
  propFilters: PropFilters,
): number {
  const busquedas = [propSearch, guestSearch].filter((t) => t.trim() !== "").length;
  const dimensiones = (Object.keys(propFilters) as (keyof PropFilters)[]).filter(
    (k) => propFilters[k].length > 0,
  ).length;
  return busquedas + dimensiones;
}

function leerPreferencia(): Preferencia {
  try {
    const crudo = JSON.parse(localStorage.getItem(CLAVE_PANEL_FILTROS) ?? "{}");
    const preferencia: Preferencia = {};
    if (typeof crudo?.movil === "boolean") preferencia.movil = crudo.movil;
    if (typeof crudo?.escritorio === "boolean") preferencia.escritorio = crudo.escritorio;
    return preferencia;
  } catch {
    return {};
  }
}

/**
 * Si el panel está abierto y cómo alternarlo.
 *
 * Sin una elección guardada, el panel parte cerrado en el celular y abierto en
 * escritorio. En el celular, con todos los controles a lo ancho, la grilla
 * quedaba varias pantallas más abajo.
 *
 * La elección se guarda en este navegador, por separado para celular y
 * escritorio. Si el navegador no deja guardar, sigue funcionando durante la
 * visita.
 */
export function usePanelFiltros(isMobile: boolean) {
  const [preferencia, setPreferencia] = useState<Preferencia>(leerPreferencia);
  const clave = isMobile ? "movil" : "escritorio";
  const abierto = preferencia[clave] ?? !isMobile;

  const alternar = () => {
    const siguiente = { ...preferencia, [clave]: !abierto };
    setPreferencia(siguiente);
    try {
      localStorage.setItem(CLAVE_PANEL_FILTROS, JSON.stringify(siguiente));
    } catch {
      // Sin almacenamiento, la elección dura solo esta visita.
    }
  };

  return { abierto, alternar };
}
